import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { LoginPage } from './pages/Login';
import { RegisterPage } from './pages/Register';
import { DashboardPage } from './pages/Dashboard';
import { HomePage } from './pages/HomePage';
import { MediaLibraryPage } from './pages/MediaLibrary';
import { FileDetailRedirectPage } from './pages/FileDetailRedirectPage';
import { AnalyticsPage } from './pages/Analytics';
import { EditorsPage } from './pages/Editors';
import { AdminPage } from './pages/Admin';
import { RBACAdminPanel } from './pages/RBACAdminPanel';
import { ActivityLogsPage } from './pages/ActivityLogs';
import { MetadataExtraction } from './pages/MetadataExtraction';
import { TeamsPageEnhanced as TeamsPage } from './pages/TeamsPageEnhanced';
import { StarredPage } from './pages/StarredPage';
import { DeletedFilesPage } from './pages/DeletedFilesPage';
import { RecentsPage } from './pages/RecentsPage';
import { SharedByMePage } from './pages/SharedByMePage';
import { SharedWithMePage } from './pages/SharedWithMePage';
import { SmartCollectionsPage } from './pages/SmartCollectionsPage';
import { PublicLinkPage } from './pages/PublicLinkPage';
import { FileRequestsPage } from './pages/FileRequestsPage';
import { PublicFileRequestPage } from './pages/PublicFileRequestPage';
import { UserSettingsPage } from './pages/UserSettings';
import { CampaignLookupPage } from './pages/CampaignLookupPage';
import { FAQPage } from './pages/FAQPage';
import { ActivityLogExportPage } from './pages/ActivityLogExport';
import { WorkloadDashboardPage } from './pages/WorkloadDashboardPage';
import { AccessRequestsPage } from './pages/AccessRequestsPage';
import { LaunchRequestsPage } from './pages/LaunchRequestsPage';
import { ScriptTrackerPage } from './pages/ScriptTrackerPage';
import { ScriptDatabasePage } from './pages/ScriptDatabasePage';
import { TrackingReportPage } from './pages/TrackingReportPage';
import { CreativeUsagePage } from './pages/CreativeUsagePage';
import { QualityCheckPage } from './pages/QualityCheckPage';
import { AnalyticsEnginePage } from './pages/AnalyticsEngine';
import { VariationStudioPage } from './pages/VariationStudio';
import { TestingTrackerPage } from './pages/TestingTracker';
import { AlertsDashboardPage } from './pages/AlertsDashboard';
import { WinnersLibrary } from './pages/WinnersLibrary';
import { UploadStatusSidebar } from './components/UploadStatusSidebar';
import { UploadNotifications } from './components/UploadNotifications';
import { DownloadProgressProvider } from './contexts/DownloadProgressContext';
import { DownloadProgressWidget } from './components/DownloadProgressWidget';
import { UploadProvider } from './contexts/UploadContext';
import { UploadProgressWidget } from './components/UploadProgressWidget';
import { CreativeTeamMonitor } from './components/CreativeTeamMonitor';

function PrivateRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return user ? children : <Navigate to="/login" />;
}

/** Root route: marketing homepage for visitors, dashboard redirect for users. */
function RootRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" replace /> : <HomePage />;
}

function PublicRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return !user ? children : <Navigate to="/" />;
}

function AdminRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const hasAdminAccess = user.role === 'admin' || user.role === 'team_lead' ||
    additionalRoles.some(r => ['admin', 'team_lead', 'ceo', 'head_media_buying', 'creative_head'].includes(r));

  if (!hasAdminAccess) {
    return <Navigate to="/" />;
  }

  return children;
}

function StrictAdminRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/" />;
  }

  return children;
}

/** Admin OR CEO only — mirrors the backend requireSuperAdmin gate on /api/winners. */
function SuperAdminRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  const additionalRoles: string[] = (user as any)?.additional_roles || [];
  const hasSuperAccess = user.role === 'admin' || user.role === 'ceo' ||
    additionalRoles.some(r => ['admin', 'ceo'].includes(r));

  if (!hasSuperAccess) {
    return <Navigate to="/" />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public link routes - no auth required */}
      <Route path="/s/:token" element={<PublicLinkPage />} />
      <Route path="/request/:token" element={<PublicFileRequestPage />} />

      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      <Route path="/" element={<RootRoute />} />
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <DashboardPage />
          </PrivateRoute>
        }
      />
      {/* Deep link route for Slack file sharing - must come before /media */}
      <Route
        path="/media/:fileId"
        element={
          <PrivateRoute>
            <FileDetailRedirectPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/media"
        element={
          <PrivateRoute>
            <MediaLibraryPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/starred"
        element={
          <PrivateRoute>
            <StarredPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/recents"
        element={
          <PrivateRoute>
            <RecentsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/trash"
        element={
          <PrivateRoute>
            <DeletedFilesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/shared-by-me"
        element={
          <PrivateRoute>
            <SharedByMePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/shared-with-me"
        element={
          <PrivateRoute>
            <SharedWithMePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <PrivateRoute>
            <AnalyticsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/editors"
        element={
          <AdminRoute>
            <EditorsPage />
          </AdminRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <StrictAdminRoute>
            <AdminPage />
          </StrictAdminRoute>
        }
      />
      <Route
        path="/rbac-admin"
        element={
          <StrictAdminRoute>
            <RBACAdminPanel />
          </StrictAdminRoute>
        }
      />
      <Route
        path="/activity-logs"
        element={
          <StrictAdminRoute>
            <ActivityLogsPage />
          </StrictAdminRoute>
        }
      />
      <Route
        path="/activity-log-export"
        element={
          <StrictAdminRoute>
            <ActivityLogExportPage />
          </StrictAdminRoute>
        }
      />
      <Route
        path="/workload"
        element={
          <PrivateRoute>
            <WorkloadDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/metadata"
        element={
          <AdminRoute>
            <MetadataExtraction />
          </AdminRoute>
        }
      />
      <Route
        path="/teams"
        element={
          <PrivateRoute>
            <TeamsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/collections"
        element={
          <PrivateRoute>
            <SmartCollectionsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/file-requests"
        element={
          <PrivateRoute>
            <FileRequestsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/access-requests"
        element={
          <PrivateRoute>
            <AccessRequestsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/launch-requests"
        element={
          <PrivateRoute>
            <LaunchRequestsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tracking-report"
        element={
          <PrivateRoute>
            <TrackingReportPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/creative-usage"
        element={
          <PrivateRoute>
            <CreativeUsagePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/quality-check"
        element={
          <PrivateRoute>
            <QualityCheckPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/script-tracker"
        element={
          <PrivateRoute>
            <ScriptTrackerPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/script-database"
        element={
          <PrivateRoute>
            <ScriptDatabasePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <UserSettingsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/campaign-lookup"
        element={
          <PrivateRoute>
            <CampaignLookupPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/faq"
        element={
          <PrivateRoute>
            <FAQPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/analytics-engine"
        element={
          <PrivateRoute>
            <AnalyticsEnginePage />
          </PrivateRoute>
        }
      />
      {/* Variation Studio — page hard-gates to admin@pearmedia.com */}
      <Route
        path="/variation-studio"
        element={
          <PrivateRoute>
            <VariationStudioPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/testing-tracker"
        element={
          <PrivateRoute>
            <TestingTrackerPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/alerts-dashboard"
        element={
          <PrivateRoute>
            <AlertsDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/winners-library"
        element={
          <SuperAdminRoute>
            <WinnersLibrary />
          </SuperAdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PermissionsProvider>
          <UploadProvider>
            <DownloadProgressProvider>
              <AppRoutes />
              <UploadStatusSidebar />
              <UploadNotifications />
              <UploadProgressWidget />
              <DownloadProgressWidget />
              <CreativeTeamMonitor />
            </DownloadProgressProvider>
          </UploadProvider>
        </PermissionsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
